import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SearchIcon, FileTextIcon } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';

const ResourceCard = ({ resource }) => {
  const handleDownload = async () => {
    const fileUrl = `https://quirkleai.com/media/${resource.file}`;
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', resource.file.split('/').pop()); // Suggest filename from URL
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download the file. Please try again.');
    }
  };

  return (
    <Card className="shadow-sm border rounded-xl bg-white">
      <CardContent className="p-4 space-y-2">
        <div className="flex justify-between items-center">
          <h4 className="font-semibold text-gray-800">{resource.title}</h4>
          <Button size="sm" variant="outline" className="text-xs flex items-center gap-1" onClick={handleDownload}>
            <FileTextIcon size={14} />
            PDF
          </Button>
        </div>
        <p className="text-sm text-gray-600">{resource.tags}</p>
      </CardContent>
    </Card>
  );
};

const ClientResources = () => {
  const { accessToken } = useAuth();
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [visibleCounts, setVisibleCounts] = useState({});

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await axios.get('https://quirkleai.com/api/resources/', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setResources(response.data);
        setFilteredResources(response.data);
      } catch (error) {
        console.error('Error fetching resources:', error);
      }
    };

    if (accessToken) {
      fetchResources();
    }
  }, [accessToken]);

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    const filtered = resources.filter(resource =>
      resource.title.toLowerCase().includes(searchTerm) ||
      resource.uploaded_by.username.toLowerCase().includes(searchTerm)
    );
    setFilteredResources(filtered);
  };

  const toggleShowMore = (therapistId) => {
    setVisibleCounts(prev => ({
      ...prev,
      [therapistId]: prev[therapistId] ? undefined : resources.filter(r => r.uploaded_by.id === therapistId).length
    }));
  };

  const groupedResources = filteredResources.reduce((acc, resource) => {
    const therapistId = resource.uploaded_by.id;
    if (!acc[therapistId]) {
      acc[therapistId] = {
        therapistName: resource.uploaded_by.username,
        resources: []
      };
    }
    acc[therapistId].resources.push(resource);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-900">Resources</h2>

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Search resources by title or therapist..."
          className="text-sm border rounded-md px-3 py-2 flex-1 focus:outline-none"
          onChange={handleSearch}
        />
        <Button variant="secondary" className="px-3 py-2">
          <SearchIcon size={16} />
        </Button>
      </div>

      {Object.values(groupedResources).map(({ therapistName, resources }) => (
        <div key={therapistName}>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">{therapistName}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.slice(0, visibleCounts[resources[0].uploaded_by.id] || 2).map(resource => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>
          {resources.length > 2 && (
            <div className="mt-4">
              <Button variant="outline" onClick={() => toggleShowMore(resources[0].uploaded_by.id)}>
                {visibleCounts[resources[0].uploaded_by.id] ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ClientResources;
